import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { FAQ_SECTIONS } from "../data/faqs";
import seoPages from "../seo/pages.json";

const APP_ONLY_PREFIXES = [
  "/dashboard",
  "/teams",
  "/surveys",
  "/analytics",
  "/chatbot",
  "/settings",
  "/compare",
  "/verify-email",
  "/assessment",
];

function absoluteUrl(pathname) {
  const path = pathname === "/" ? "/" : pathname.replace(/\/$/, "");
  return `${seoPages.siteUrl}${path}`;
}

function matchPage(pathname) {
  const exact = seoPages.pages.find((page) => page.path === pathname);
  if (exact) return exact;
  if (pathname.startsWith("/success-stories/")) {
    return {
      title: "Success Story | Udvega",
      description:
        "How organizations use Udvega HR analytics to reduce turnover and protect employee privacy.",
    };
  }
  return null;
}

function isPrivatePath(pathname) {
  return APP_ONLY_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Udvega",
    url: seoPages.siteUrl,
    logo: seoPages.ogImage,
    email: "info@udvega.com",
    description: seoPages.defaultDescription,
    sameAs: [],
  };
}

function softwareSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Udvega",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    url: seoPages.siteUrl,
    description: seoPages.defaultDescription,
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  };
}

function faqSchema() {
  const entities = FAQ_SECTIONS.flatMap((section) =>
    section.items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    }))
  );
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: entities,
  };
}

export default function Seo({ title, description, image, noindex, children }) {
  const { pathname } = useLocation();
  const page = matchPage(pathname);
  const privatePath = isPrivatePath(pathname);
  const resolvedTitle = title || page?.title || seoPages.defaultTitle;
  const resolvedDescription =
    description || page?.description || seoPages.defaultDescription;
  const resolvedImage = image || seoPages.ogImage;
  const canonical = absoluteUrl(pathname);
  const robots =
    noindex || privatePath
      ? "noindex, nofollow"
      : page?.robots || "index, follow";
  const isPublic = !privatePath;

  const schemas = [];
  if (isPublic) {
    schemas.push(organizationSchema());
    if (pathname === "/") schemas.push(softwareSchema());
    if (pathname === "/faq") schemas.push(faqSchema());
  }

  return (
    <Helmet prioritizeSeoTags>
      <html lang="en" />
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={seoPages.siteName} />
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={resolvedImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={resolvedImage} />
      {schemas.map((schema) => (
        <script key={schema["@type"]} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
      {children}
    </Helmet>
  );
}

export { isPrivatePath, seoPages };
